import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AiOutlineDoubleLeft } from 'react-icons/ai';
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import {
    MdOutlineEdit,
    MdOutlineInfo
} from 'react-icons/md';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import Table from 'components/Table';
import Popup from 'components/Popup';
import TestConfigPopup from 'components/TestConfigPopup';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import { SUBJECTS, buildInitialConfig } from 'global/testConfig';

import './index.css';

type TestsProps = {
    studs: any[];
    tests: any[];
    testType: string;
    setTests: ({}) => void;
    updateTests: boolean;
    archiveSelected: boolean;
};

function Tests({
    studs,
    tests,
    testType,
    setTests,
    updateTests,
    archiveSelected
}: TestsProps) {
    // The class test whose config popup is open. 'assign' opens from the
    // Assign list, 'update' from the pencil on an assigned test.
    const [configTarget, setConfigTarget] = useState<{ testId: string; mode: 'assign' | 'update' } | null>(null);
    // Set after an update that skipped students who had already started.
    const [skippedNotice, setSkippedNotice] = useState('');

    const { classId } = useParams();

    const navigate = useNavigate();
    const { user } = useAuthContext();
    const skippedNoticeRef = useOutsideClickHandler(() => setSkippedNotice(''));

    const configTest = configTarget && tests.find(test => test.testId === configTarget.testId);

    const getTestSectionLabel = (test) => {
        const assignedSubjects = test.testSections.filter(section => section.assigned).map(section => section.subject);

        const rw = assignedSubjects.includes('Reading and Writing');
        const math = assignedSubjects.includes('Math');
        const hasEng = assignedSubjects.includes('ACT English');
        const hasMath = assignedSubjects.includes('ACT Mathematics');
        const hasReading = assignedSubjects.includes('ACT Reading');
        const hasScience = assignedSubjects.includes('ACT Science');
        const shsatEnglish = assignedSubjects.includes('English Language Arts');
        const shsatMath = assignedSubjects.includes('Mathematics');
        const tsia2Elar = assignedSubjects.includes('TSIA2 ELAR');
        const tsia2Math = assignedSubjects.includes('TSIA2 Mathematics');

        if ((rw && math) || (shsatEnglish && shsatMath) || (tsia2Elar && tsia2Math)) {
            return 'Full';
        } else if (hasEng && hasMath && hasReading && hasScience) {
          return 'Full';
        } else {
          return assignedSubjects
            .map(subject => SUBJECTS[subject])
            .join(', ');
        }
      };

    // A class test's current settings, in the shape the config popup reads.
    // Timing has no class-level value, so it starts on "keep current timing".
    const configExisting = (test) => ({
        testSections: test.testSections.filter(section => section.assigned).map(section => ({
            subject: section.subject,
            moduleTwoDifficulty: section.moduleTwoDifficulty
        })),
        timeExtension: null,
        ttsEnabled: test.ttsEnabled,
        administrationMode: test.administrationMode
    });

    // Resolves once the list is refreshed; rejects with the server error so
    // the popup can show it.
    const submitConfig = (payload) => (
        axios.post(configTarget.mode === 'assign' ? 'tutors/classes/test/assign/' : 'tutors/classes/test/configure/', {
                ...payload,
                classId: classId,
                testId: configTarget.testId
            })
            .then(res => {
                setTests(res.data.tests);
                setConfigTarget(null);
                if (res.data.skippedStarted > 0) {
                    setSkippedNotice(
                        `${res.data.skippedStarted} ${res.data.skippedStarted === 1 ? 'student has' : 'students have'} already started this test, so their assignment was not changed.`
                    );
                }
            })
    );

    const removeTest = (testId: string) => {
        axios.post('tutors/classes/test/remove/', {
                classId: classId,
                testId: testId
            })
            .then(res => {
                setTests(res.data.tests);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const hideTestReview = (testId: string) => {
        axios.post('tutors/classes/test/hideReview/', {
                classId: classId,
                testId: testId
            })
            .then(res => {
                // setTests(res.data.tests);
                let newTests = [ ...tests ];
                const testIndex = newTests.findIndex(test => test.testId == testId);

                newTests[testIndex].testReview = false;
                setTests(newTests);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const showTestReview = (testId: string) => {
        axios.post('tutors/classes/test/showReview/', {
                classId: classId,
                testId: testId
            })
            .then(res => {
                let newTests = [ ...tests ];
                const testIndex = newTests.findIndex(test => test.testId == testId);

                newTests[testIndex].testReview = true;
                setTests(newTests);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const matchesArchive = (test) => archiveSelected
        ? !!test.archived
        : test.type === testType && !test.archived;

    const assignedTests = tests.filter(test => test.assignDate).sort((a, b) => (
        new Date(b.assignDate).getTime() - new Date(a.assignDate).getTime()
    ));

    return (
        <>
            {archiveSelected && (
                <div className='class-tests__archiveNote'>
                    <MdOutlineInfo />
                    <span>These are retired tests, kept available for ongoing assignments.</span>
                </div>
            )}
            {configTest &&
                <TestConfigPopup
                    test={configTest}
                    initialConfig={buildInitialConfig(
                        configTest,
                        configTarget.mode === 'update' ? configExisting(configTest) : null,
                        null
                    )}
                    mode={configTarget.mode}
                    audience='class'
                    onSubmit={submitConfig}
                    onClose={() => setConfigTarget(null)}
                />
            }
            {skippedNotice &&
                <Popup
                    title='Updated'
                    primaryButtonText='OK'
                    primaryButtonOnClick={() => setSkippedNotice('')}
                    innerRef={skippedNoticeRef}
                >
                    {skippedNotice}
                </Popup>
            }
            <div className='class-tests'>
                {updateTests
                ?   <>
                        <Table
                            key={`remove_${testType}_${archiveSelected ? 'archive' : 'active'}`}
                            className='class-tests__table'
                            header={['', 'Name']}
                            rows={assignedTests.filter(matchesArchive).map(test => ([
                                <p className='class-tests__remove'>Remove</p>,
                                <span className="class-tests__testsContent">
                                    {test.name} {test.isNew && <span className="class-tests__testsnewBadge">NEW</span>}
                                </span>,
                            ]))}
                            onSelectCell={assignedTests.filter(matchesArchive).map(test => ([
                                () => removeTest(test.testId),
                                null,
                                null
                            ]))}
                        />
                        <AiOutlineDoubleLeft className='class-tests__arrow' />
                        <Table
                            key={`assign_${testType}_${archiveSelected ? 'archive' : 'active'}`}
                            className='class-tests__table'
                            header={['', 'Name']}
                            rows={tests.filter(test => !test.assignDate && matchesArchive(test)).map(test => ([
                                'Assign',
                                <span className="class-tests__testsContent">
                                    {test.name} {test.isNew && <span className="class-tests__testsnewBadge">NEW</span>}
                                </span>,
                            ]))}
                            onSelectCell={tests.filter(test => !test.assignDate && matchesArchive(test)).map(test => ([
                                () => setConfigTarget({ testId: test.testId, mode: 'assign' }),
                                null,
                                null
                            ]))}
                        />
                    </>
                :   <div className='class-tests__tests'>
                        <Table
                            className='class-tests__table'
                            header={[
                                '',
                                'Name',
                                'Sections',
                                '# Assigned',
                                'Assigned Date',
                                ... user.customizations.find(customization => customization === 'Hide Report') ? ['Show/Hide Report'] : [],
                                studs.length > 0? 'Report': '-'
                            ]}
                            rows={assignedTests.map(test => ([
                                <MdOutlineEdit
                                    className='class-tests__configure'
                                    onClick={() => setConfigTarget({ testId: test.testId, mode: 'update' })}
                                />,
                                test.name,
                                getTestSectionLabel(test),
                                test.numAssigned,
                                new Date(test.assignDate).toLocaleDateString(),
                                ... user.customizations.find(customization => customization === 'Hide Report')
                                    ? [test.testReview
                                        ? <IoEyeOutline className='class-tests__show' />
                                        : <IoEyeOffOutline className='class-tests__hide' />
                                    ] : [],
                                'Report'
                            ]))}
                            onSelectCell={assignedTests.map(test => ([
                                () => {},
                                test.type === 'Digital SAT' || test.type === 'Digital PSAT'
                                ? () => navigate(`/tests/digital-sat/${test.testId}`)
                                : test.type === 'BCA'
                                ? () => navigate(`/tests/digital-bca/${test.testId}`)
                                : test.type === 'Digital ACT'
                                ? () => navigate(`/tests/digital-act/${test.testId}`)
                                : test.type === 'Digital SHSAT'
                                ? () => navigate(`/tests/digital-shsat/${test.testId}`)
                                : test.type === 'TSIA2'
                                ? () => navigate(`/tests/digital-tsia2/${test.testId}`)
                                : test.type === 'Paper ACT'
                                ? () => navigate(`/tests/paper-act/${test.testId}`)
                                : null,
                                null,
                                null,
                                null,
                                ... user.customizations.find(customization => customization === 'Hide Report')
                                    ? [test.testReview
                                        ? () => hideTestReview(test.testId)
                                        : () => showTestReview(test.testId)
                                    ] : [],
                                studs.length > 0? (test.type === 'Digital SAT' || test.type === 'Digital PSAT'
                                ? () => navigate(`/classes/digital-sat/${classId}/${test.name}`)
                                : test.type === 'BCA'
                                ? () => navigate(`/classes/digital-bca/${classId}/${test.name}`)
                                : test.type === 'Digital ACT'
                                ? () => navigate(`/classes/digital-act/${classId}/${test.name}`)
                                : test.type === 'Digital SHSAT'
                                ? () => navigate(`/classes/digital-shsat/${classId}/${test.name}`)
                                : test.type === 'TSIA2'
                                ? () => navigate(`/classes/digital-tsia2/${classId}/${test.name}`)
                                : test.type === 'Paper ACT'
                                ? () => navigate(`/classes/paper-act/${classId}/${test.name}`)
                                : null): null,
                            ]))}
                        />
                    </div>
                }
            </div>
        </>
    );
}

export default Tests;
